import { RoleName } from "consts/auth";
import type { User } from "features/users/types";
import type { BooleanSwitch, ID, ResourceResponse } from "types";

// User fields the API returns in the login/refresh body (stored in authData)
export interface AuthUserPayload {
    readonly merchantIds: Array<ID>;
    readonly mors?: Array<unknown>;
    readonly permissions: Array<unknown>;
    readonly userId: ID;
    readonly roleId: number;
}

export type UserCredentials = {
  readonly email: string;
  readonly password: string;
};

export type NewPasswordCredentials = Omit<UserCredentials, 'email'> & {
  readonly email?: string;
  readonly token: string;
};

export enum AuthStatus {
  Authenticated = 'authenticated',
  Guest = 'guest',
  Pending = 'pending',
  Uninitialized = 'uninitialized'
};

export type AuthState<T = User> = {
  status: AuthStatus;
  user: T | null;
};

export type AuthResponse = ResourceResponse<Partial<AuthUserPayload> & {
  // Still sent by the API during the cookie transition, never stored
  readonly access_token?: string | null;
  readonly role_name: RoleName;
  readonly token_type: string;
  readonly expires_in: number;
  readonly twoFactorAuth: boolean;
}>;

export type AuthData = Omit<AuthResponse['data'], 'access_token'> & {
  readonly timestamp: number;
};

export type Authorizable = {
  readonly roles?: Array<RoleName>;
};

export type TwoFactorCode = number | string;

export type TwoFactorSetup = {
  readonly twoFactorEnabled: BooleanSwitch;
};

export type TwoFactorVerify = {
  readonly twoFactorCode: TwoFactorCode;
  readonly withTrustToken?: boolean;
};

export type TwoFactorVerifyResponse = ResourceResponse<{
  readonly trustToken: string | null;
}>;

export type WithTwoFactor<T extends object> = TwoFactorSetup & T;
